/**
 * DealPage — `/deals/:id`. Карточка конкретной сделки воронки КОММА (Bitrix24).
 *
 * Отвечает на вопрос «что это за сделка»: название, стадия, сумма, ОТКУДА пришла
 * (UTM источник/кампания/объявление) и контакт. Открывается из «Последних сделок»
 * на Обзоре.
 */

import { useParams, Link } from '@tanstack/react-router';
import { usePageHeader } from '@/components/layout/pageHeader';
import { Badge, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { useDeal, type DealStatus } from '@/hooks/api/useDeals';

const TONE: Record<DealStatus, 'b-success' | 'b-danger' | 'b-neutral'> = {
  won: 'b-success',
  lost: 'b-danger',
  active: 'b-neutral',
};
const STATUS_LABEL: Record<DealStatus, string> = { won: 'Выиграна', lost: 'Проиграна', active: 'В работе' };

function rub(v: number | null | undefined, currency = 'RUB'): string {
  if (!v) return '—';
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: currency || 'RUB', maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${new Intl.NumberFormat('ru-RU').format(v)} ${currency}`;
  }
}
function dt(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function DealPage() {
  const { id } = useParams({ from: '/_authed/deals/$id' });
  usePageHeader({ title: 'Сделка', subtitle: `Карточка сделки #${id}` });
  const { data, isLoading, isError, error, refetch } = useDeal(id);
  const deal = data?.deal;

  return (
    <div className="col gap20" style={{ maxWidth: 880 }}>
      <Link to="/overview" style={{ color: 'var(--primary)', fontSize: 13 }}>
        ← К обзору
      </Link>

      {isLoading && <LoadingBlock label="Загружаю сделку…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}
      {data && !data.configured && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>CRM (Bitrix24) не подключён.</p>
        </div>
      )}

      {deal && (
        <>
          <section className="card">
            <div className="row spread wrap" style={{ gap: 8, alignItems: 'flex-start' }}>
              <div className="col" style={{ gap: 4 }}>
                <h2 style={{ margin: 0 }}>{deal.title || `Сделка #${deal.id}`}</h2>
                <span className="muted mono" style={{ fontSize: 12 }}>#{deal.id}</span>
              </div>
              <Badge tone={TONE[deal.status]}>{deal.stageName || STATUS_LABEL[deal.status]}</Badge>
            </div>
            <div className="stat-grid" style={{ marginTop: 16 }}>
              <Field label="Сумма" value={rub(deal.opportunity, deal.currency)} />
              <Field label="Вероятность" value={deal.probability ? `${deal.probability}%` : '—'} />
              <Field label="Создана" value={dt(deal.dateCreate)} />
              <Field label="Изменена" value={dt(deal.dateModify)} />
            </div>
          </section>

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Откуда пришла (атрибуция)</h3>
            {deal.utmSource || deal.utmCampaign ? (
              <table className="tbl">
                <tbody>
                  <KV k="Источник (utm_source)" v={deal.utmSource} />
                  <KV k="Канал (utm_medium)" v={deal.utmMedium} />
                  <KV k="Кампания (utm_campaign)" v={deal.utmCampaign} />
                  <KV k="Объявление (utm_content)" v={deal.utmContent} />
                  <KV k="Ключ (utm_term)" v={deal.utmTerm} />
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                UTM-метки не сохранились в сделке (проброс UTM в CRM — открытая задача, см. ТЗ лендинга).
              </p>
            )}
          </section>

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Контакт</h3>
            {deal.contact ? (
              <table className="tbl">
                <tbody>
                  <KV k="Имя" v={deal.contact.name} />
                  <KV k="Должность" v={deal.contact.post} />
                  <KV k="Телефон" v={deal.contact.phone} />
                  <KV k="Почта" v={deal.contact.email} />
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Контакт не привязан к сделке.</p>
            )}
          </section>

          {deal.comments && (
            <section className="card">
              <h3 style={{ marginTop: 0 }}>Комментарий</h3>
              <div style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{deal.comments}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: 18 }}>{value}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <tr>
      <td className="muted" style={{ width: 220, fontSize: 13 }}>{k}</td>
      <td style={{ fontSize: 13.5 }}>{v || '—'}</td>
    </tr>
  );
}
